import { AiPromptSummaryResponse } from '@ydistri/api-sdk';
import {
  ActionsBar,
  Button,
  DataTable,
  IconButton,
  IconButton_IconSizes,
  SearchBox,
  Section,
  Text,
} from '@ydistri/ds';
import React, { useCallback, useMemo, useState } from 'react';
import { MdAdd, MdHistory } from 'react-icons/md';
import DeleteIconButton from '../../../components/buttons/DeleteIconButton';
import EditIconButton from '../../../components/buttons/EditIconButton';
import { useAppDispatch } from '../../../store';
import { AvailableModals, openModal } from '../../Modals/modalSlice';
import { getAiPromptColumns } from './aiPromptsColumns';
import { matchesTitle } from './aiPromptsLib';
import { useGetAiPromptsQuery } from './apiAiPrompts';

const createIcon = <MdAdd size={IconButton_IconSizes.small} />;
const historyIcon = <MdHistory size={IconButton_IconSizes.small} />;

const AiPromptsSection: React.FC = () => {
  const dispatch = useAppDispatch();
  const [search, setSearch] = useState<string>('');

  const { data: aiPrompts, isFetching, isError } = useGetAiPromptsQuery();

  const displayedData = useMemo(
    () => (aiPrompts ?? []).filter(prompt => matchesTitle(prompt, search)),
    [aiPrompts, search],
  );

  const openEditModal = useCallback(
    (aiPrompt?: AiPromptSummaryResponse) => {
      dispatch(openModal({ type: AvailableModals.AI_PROMPT_EDIT, data: { aiPrompt } }));
    },
    [dispatch],
  );

  const openDeleteModal = useCallback(
    (aiPrompt: AiPromptSummaryResponse) => {
      dispatch(openModal({ type: AvailableModals.AI_PROMPT_DELETE, data: { aiPrompt } }));
    },
    [dispatch],
  );

  const openHistoryModal = useCallback(
    (aiPrompt: AiPromptSummaryResponse) => {
      dispatch(openModal({ type: AvailableModals.AI_PROMPT_HISTORY, data: { aiPrompt } }));
    },
    [dispatch],
  );

  const renderActions = useCallback(
    (record: AiPromptSummaryResponse) => (
      <ActionsBar>
        <IconButton
          icon={historyIcon}
          size="small"
          tooltip="History"
          tooltipPosition="left"
          onClick={() => openHistoryModal(record)}
        />
        <EditIconButton size="small" tooltip="Edit" onClick={() => openEditModal(record)} />
        <DeleteIconButton size="small" tooltip="Delete" onClick={() => openDeleteModal(record)} />
      </ActionsBar>
    ),
    [openEditModal, openDeleteModal, openHistoryModal],
  );

  const columns = useMemo(() => getAiPromptColumns({ renderActions }), [renderActions]);

  const headerActions = useMemo(
    () => (
      <ActionsBar>
        <SearchBox
          value={search}
          onSearch={setSearch}
          placeholder="Title"
          id="ai-prompts-search-box"
        />
        <Button
          $textVariant="semibold"
          icon={createIcon}
          type="primary"
          onClick={() => openEditModal()}
          data-testid="create_new_ai_prompt"
        >
          New prompt
        </Button>
      </ActionsBar>
    ),
    [search, openEditModal],
  );

  return (
    <Section header="AI Prompts" headerActions={headerActions} data-testid="AiPromptsSection">
      {isError ? (
        // An empty table would otherwise read as "there are no prompts".
        <Text $type="danger">The AI prompts could not be loaded.</Text>
      ) : (
        <DataTable<AiPromptSummaryResponse>
          showBorder={true}
          showHeader={true}
          id="ai-prompts"
          loading={isFetching}
          columns={columns}
          dataSource={displayedData}
          size="small"
          rowKey="aiPromptId"
        />
      )}
    </Section>
  );
};

export default AiPromptsSection;
